import { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonIcon,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {
  people,
  location,
  trendingUp,
  shieldCheckmark,
  sparkles,
  checkmarkDone,
  search,
} from 'ionicons/icons';
import { LogoLink } from '@/components/LogoLink';
import { AgentCard } from '@/components/agent/AgentCard';
import { AgentSearchBar } from '@/components/agent/AgentSearchBar';
import { AppFooter } from '@/components/AppFooter';
import { useAgents } from '@/lib/api/hooks';
import { setSeo, resetSeo, BASE_URL } from '@/lib/seo';
import './Agents.css';

const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1400&h=900&fit=crop&q=80',
  'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1400&h=900&fit=crop&q=80',
  'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1400&h=900&fit=crop&q=80',
];

const STEPS = [
  { icon: location, title: 'Cover Your Area', desc: 'Define your geographic coverage zone using H3 proximity matching. We connect you with candidates and employers in your neighbourhood.' },
  { icon: checkmarkDone, title: 'Onboard & Support', desc: 'Help candidates set up profiles, guide employers through hiring, and build trust at community level.' },
  { icon: trendingUp, title: 'Grow the Marketplace', desc: 'Earn referrals as your network grows. Track your impact through NPS scores and assignment metrics.' },
];

const FEATURES = [
  { icon: shieldCheckmark, title: 'Verified Trust', desc: 'Every agent is vetted and verified. Candidates and employers know they\'re working with someone they can trust.' },
  { icon: sparkles, title: 'AI-Powered Matching', desc: 'H3 proximity matching finds the right agent for each candidate automatically. No manual sorting needed.' },
  { icon: people, title: 'Community Impact', desc: 'Build real relationships in your area. You\'re not just a platform user — you\'re a local relationship owner.' },
];

export function AgentsPage() {
  const history = useHistory();
  const [heroIdx, setHeroIdx] = useState(0);
  const [searchResult, setSearchResult] = useState<any[] | null>(null);
  const [searchPlaceName, setSearchPlaceName] = useState<string | null>(null);

  const { data: agentsData, isLoading } = useAgents({ status: 'Active' });
  const allAgents = agentsData?.data ?? [];

  // Use search results if available, otherwise show all agents
  const displayAgents = searchResult ?? allAgents;

  const handleSearchResult = useCallback((agents: any[], placeName: string) => {
    setSearchResult(agents);
    setSearchPlaceName(placeName);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchResult(null);
    setSearchPlaceName(null);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setSeo({
      title: 'Service Agents | BluePages Local Marketplace Partners',
      description: 'Find a BluePages Service Agent in your area. Trusted local partners who support candidates and employers across South Africa.',
      canonical: `${BASE_URL}/agents`,
      url: `${BASE_URL}/agents`,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'BluePages Service Agents',
        description: 'Find trusted Service Agents in your area.',
        url: `${BASE_URL}/agents`,
      },
    });
    return () => resetSeo();
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <LogoLink />
          <IonTitle>Service Agents</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* Hero */}
        <div className="bp-hero" style={{ position: 'relative' }}>
          <div className="bp-hero-bg" aria-hidden="true">
            {HERO_IMAGES.map((src, i) => (
              <div
                key={src}
                className="bp-hero-slide"
                style={{
                  backgroundImage: `url('${src}')`,
                  opacity: i === heroIdx ? 1 : 0,
                  transform: i === heroIdx ? 'scale(1.08)' : 'scale(1)',
                }}
              />
            ))}
            <div className="bp-hero-slide-overlay" />
          </div>
          <div className="bp-glow-orb bp-glow-orb-1" />
          <div className="bp-glow-orb bp-glow-orb-2" />

          <div className="bp-hero-content">
            <div className="bp-badge" style={{ marginBottom: '1.25rem' }}>
              <IonIcon icon={sparkles} style={{ fontSize: '0.625rem' }} />
              Now Recruiting
            </div>

            <h1 className="bp-heading" style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>
              Become a{' '}
              <span style={{ background: 'var(--bp-gradient-accent)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Service Agent
              </span>
            </h1>

            <p className="bp-subheading" style={{ marginBottom: '1.75rem', maxWidth: '480px' }}>
              Own your local marketplace. Onboard candidates, support employers, and grow South Africa's
              contract workforce — starting in your neighbourhood.
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                className="bp-hero-cta"
                onClick={() => history.push('/agents/apply')}
              >
                Apply Now
              </button>
              <button
                className="bp-hero-cta-secondary"
                onClick={() => document.getElementById('agent-directory')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Find an Agent
              </button>
            </div>
          </div>
        </div>

        {/* Agent Directory */}
        <section id="agent-directory" className="bp-section" style={{ background: 'var(--bp-bg)' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="bp-heading" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
              Find an Agent Near You
            </h2>
            <p className="bp-subheading" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              Search by area, town, or province to find your local Service Agent.
            </p>

            <AgentSearchBar onResult={handleSearchResult} onClear={handleSearchClear} />

            {searchPlaceName && (
              <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--bp-text-muted)', margin: '0 0 1rem' }}>
                Showing agents near <strong>{searchPlaceName}</strong>
              </p>
            )}

            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <IonSpinner />
              </div>
            ) : displayAgents.length > 0 ? (
              <div className="bp-agent-grid">
                {displayAgents.map((agent: any) => (
                  <AgentCard
                    key={agent._id}
                    agent={{
                      id: agent._id,
                      slug: agent.slug,
                      displayName: agent.displayName,
                      bio: agent.bio,
                      area: agent.area,
                      assignedCandidates: agent.assignedCandidates,
                      assignedEmployers: agent.assignedEmployers,
                      npsScore: agent.npsScore,
                    }}
                  />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <IonIcon icon={search} style={{ fontSize: '2rem', color: 'var(--bp-text-muted)', marginBottom: '0.5rem' }} />
                <IonText color="medium">
                  <p style={{ margin: 0 }}>
                    {searchPlaceName
                      ? `No agents found near ${searchPlaceName}.`
                      : 'No agents registered yet. Be the first!'}
                  </p>
                </IonText>
                {!searchPlaceName && (
                  <button
                    className="bp-hero-cta"
                    style={{ marginTop: '1rem' }}
                    onClick={() => history.push('/agents/apply')}
                  >
                    Apply as Agent
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* How It Works */}
        <section className="bp-section bp-section-alt">
          <h2 className="bp-heading" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>How It Works</h2>
          <div style={{ display: 'grid', gap: '1rem', maxWidth: '800px', margin: '0 auto' }}>
            {STEPS.map((step, i) => (
              <div key={i} className="bp-card" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', padding: '1rem' }}>
                <span className="bp-badge" style={{ flexShrink: 0, width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                  {i + 1}
                </span>
                <div>
                  <h3 style={{ margin: 0, fontWeight: 600, color: 'var(--bp-text)' }}>{step.title}</h3>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: 'var(--bp-text-secondary)', lineHeight: 1.5 }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="bp-section">
          <h2 className="bp-heading" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Why BluePages Agents Win</h2>
          <div style={{ display: 'grid', gap: '1rem', maxWidth: '800px', margin: '0 auto' }}>
            {FEATURES.map((feat, i) => (
              <div key={i} className="bp-feature-card" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <IonIcon icon={feat.icon} className="bp-feature-icon" />
                <div>
                  <h3 className="bp-feature-title">{feat.title}</h3>
                  <p className="bp-feature-desc">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bp-section bp-cta">
          <h2 className="bp-heading" style={{ textAlign: 'center', color: '#fff' }}>Ready to Own Your Area?</h2>
          <p className="bp-subheading" style={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)', marginBottom: '1.5rem' }}>
            Apply today and join a growing network of trusted local agents across South Africa.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
            <button
              className="bp-hero-cta"
              onClick={() => history.push('/agents/apply')}
            >
              Apply Now
            </button>
            <button
              className="bp-hero-cta-secondary"
              onClick={() => document.getElementById('agent-directory')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Find an Agent
            </button>
          </div>
        </section>

        <AppFooter />
      </IonContent>
    </IonPage>
  );
}
